import { useCallback, useEffect, useRef, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import AgentsMapComponents from "./mapComponets";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import FilterAltOffRounded from "@mui/icons-material/FilterAltOffRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { gql } from "@apollo/client";
import request from "graphql-request";
import BASEURL from "../../../pages/Utils/Baseurl";
import webSocketUrl from "../../../pages/Utils/webSocketUrl";
import MultiSelectAutocomplete from "../../../components/MultiSelectAutocomplete/index";
import MaterialSelect from "../../../components/MasterSelect";
import CircleInfo from "./info/CircleInfo";
import MaterialInput from "../../../components/MaterialInput";

const getDeliveryPersonCheckinQuery = gql`
  query GetCheckIn($employeeId: Int, $deliveryDate: Date) {
    getDeliveryPersonCheckin(
      employeeId: $employeeId
      deliveryDate: $deliveryDate
    ) {
      success
      message
      totalCount
      visitDate
      data {
        id
        latitude
        longitude
        checkinLocationLat
        checkinLocationLng
        checkInTime
        visitDate
        employeeId
        employeeName
        employeeCode
        role
        deliveryNo
        customerName
        deliveryStatus
      }
    }
  }
`;

const DeliveryMapView = () => {
  const [data, setData] = useState([]);
  const [agentOptions, setAgentoptions] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [value, setValue] = useState(0);
  const [selectAgent, setSelectAgent] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [checkinData, setCheckinData] = useState([]);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinError, setCheckinError] = useState("");
  const socket = useRef(null);
  const lastRequest = useRef(null);
  const checkinRequestId = useRef(0);

  const employeeOptions = employees.map((employee) => ({
    value: employee.id,
    label: employee.employeeCode
      ? `${employee.employeeName} (${employee.employeeCode})`
      : employee.employeeName,
  }));

  const fetchCheckins = useCallback(async () => {
    const requestId = checkinRequestId.current + 1;
    checkinRequestId.current = requestId;
    setCheckinLoading(true);
    setCheckinError("");

    try {
      const result = await request(BASEURL, getDeliveryPersonCheckinQuery, {
        employeeId: selectedEmployee?.value
          ? Number(selectedEmployee.value)
          : null,
        deliveryDate: deliveryDate || null,
      });
      const response = result?.getDeliveryPersonCheckin;

      if (!response?.success) {
        throw new Error(response?.message || "Unable to load delivery check-ins.");
      }

      if (requestId === checkinRequestId.current) {
        const records = response?.data || [];
        setCheckinData(records);

        if (!selectedEmployee) {
          const uniqueEmployees = new Map();
          records.forEach((record) => {
            if (
              record?.employeeId != null &&
              !uniqueEmployees.has(record.employeeId)
            ) {
              uniqueEmployees.set(record.employeeId, {
                id: record.employeeId,
                employeeName: record.employeeName,
                employeeCode: record.employeeCode,
              });
            }
          });
          setEmployees(Array.from(uniqueEmployees.values()));
        }
      }
    } catch (error) {
      if (requestId === checkinRequestId.current) {
        setCheckinData([]);
        setCheckinError(
          error?.response?.errors?.[0]?.message ||
          error?.message ||
          "Unable to load delivery check-ins.",
        );
      }
    } finally {
      if (requestId === checkinRequestId.current) {
        setCheckinLoading(false);
      }
    }
  }, [deliveryDate, selectedEmployee]);

  useEffect(() => {
    if (value === 0) {
      fetchCheckins();
    }
  }, [fetchCheckins, value]);

  useEffect(() => {
    setFilteredData([]);
    socket.current = new WebSocket(`${webSocketUrl}/delivery-tracking/`);

    socket.current.onopen = () => {
      console.log("✅ WebSocket Connected");
    };

    socket.current.onmessage = async (event) => {
      let response;
      try {
        response = JSON.parse(event.data);
      } catch (error) {
        console.error("Unable to parse delivery tracking update:", error);
        return;
      }
      const receivedData = response?.data || [];
      const receivedAgents = response?.agents || [];

      setData((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(receivedData)) {
          setFilteredData(receivedData);
          return receivedData;
        }
        return prev;
      });
      setAgentoptions((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(receivedAgents)) {
          return receivedAgents;
        }
        return prev;
      });
    };

    socket.current.onerror = (error) => {
      console.error("❌ WebSocket Error:", error);
    };

    socket.current.onclose = () => {
      console.log("❌ WebSocket Disconnected");
    };

    return () => {
      if (socket.current) socket.current.close();
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);

    if (newValue === 0) {
      setFilteredData(data);
    } else if (newValue === 1) {
      const agentsOnly = data?.map((agent) => ({
        ...agent,
        customers: [],
      }));

      setFilteredData(agentsOnly);
    } else if (newValue === 2) {
      const customersOnly = data?.map((agent) => ({
        agentId: agent?.agentId,
        agentName: agent?.agentName,
        customers: agent?.customers,
      }));
      setFilteredData(customersOnly);
    }
  };

  const clearCheckinFilters = () => {
    setSelectedEmployee(null);
    setDeliveryDate("");
  };

  const handleDateChange = (event) => {
    setDeliveryDate(event.target.value);
    setSelectedEmployee(null);
  };

  const handleMultipleSelect = (event, newValue) => {
    const ids = newValue?.map((v) => v?.id);
    setSelectAgent(newValue);

    const payload = {
      type: "filter",
      agents: ids,
    };

    // 🧠 Only send if payload is different from lastRequest
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      if (JSON.stringify(payload) !== JSON.stringify(lastRequest.current)) {
        lastRequest.current = payload;
        socket.current.send(JSON.stringify(payload));
      } else {
        console.log("⚠️ Skipped duplicate WebSocket send");
      }
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: "#258f43",
              bgcolor: "rgba(42, 153, 65, 0.1)",
            }}
          >
            <LocationOnOutlinedIcon />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: 21,
                  fontWeight: 700,
                  color: "#252a2e",
                  lineHeight: 1.3,
                }}
              >
                Delivery Agent and Driver Map
              </Typography>
              <CircleInfo />
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Track delivery check-ins and customer visits
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 1, sm: 1.5 },
            py: 1,
            mb: 1.5,
            bgcolor: "#fff",
            border: "1px solid #e6e9ed",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(31, 45, 61, 0.04)",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              bgcolor: "#f3f5f7",
              borderRadius: 1.5,
              p: 0.5,
              flex: { xs: "1 1 100%", md: "0 0 auto" },
            }}
          >
            <Tabs
              value={value}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                minHeight: 38,
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: 38,
                  minWidth: { xs: 0, md: 112 },
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  color: "#606971",
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "#fff",
                  bgcolor: "#299b47",
                  boxShadow: "0 2px 5px rgba(41, 155, 71, 0.25)",
                },
              }}
            >
              {/* <Tab label="All" /> */}
              <Tab label="Agent" />
              {/* <Tab label="Customer" /> */}
            </Tabs>
          </Box>

          {value === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "stretch", md: "flex-end" },
                gap: 1,
                flex: "1 1 420px",
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: "1 1 220px", minWidth: 0, maxWidth: 340 }}>
                <MaterialSelect
                  size="small"
                  options={employeeOptions}
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  isClearable
                  isLoading={checkinLoading && employees.length === 0}
                  placeholder="All delivery persons"
                  aria-label="Filter by employee"
                />
              </Box>
              <MaterialInput
                size="small"
                type="date"
                label="Visit date"
                value={deliveryDate}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ "aria-label": "Filter by delivery date" }}
                sx={{
                  flex: "0 1 190px",
                  minWidth: 155,
                  "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                }}
              />
              <Tooltip title="Clear filters">
                <span>
                  <IconButton
                    aria-label="Clear filters"
                    onClick={clearCheckinFilters}
                    disabled={!selectedEmployee && !deliveryDate}
                    sx={{
                      width: 40,
                      height: 40,
                      border: "1px solid #e0e4e8",
                      borderRadius: 1.5,
                      color: "#5d6872",
                      "&:hover": { color: "#c0392b", bgcolor: "#fff4f2" },
                    }}
                  >
                    <FilterAltOffRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Box
              sx={{
                width: { xs: "100%", md: 300 },
                flex: { xs: "1 1 100%", md: "0 1 300px" },
              }}
            >
              {value !== 2 && (
                <MultiSelectAutocomplete
                  id=""
                  placeholder="Delivery Persons"
                  options={agentOptions}
                  value={selectAgent}
                  onChange={handleMultipleSelect}
                  labelKey="name"
                />
              )}
            </Box>
          )}
        </Box>

        {value === 0 && checkinError && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5 }}>
            {checkinError}
          </Alert>
        )}
        {value === 0 && checkinLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 1,
              color: "text.secondary",
            }}
          >
            <CircularProgress size={24} aria-label="Loading check-ins" />
            <Typography variant="body2">Loading map locations...</Typography>
          </Box>
        )}
        <Box
          sx={{
            overflow: "hidden",
            border: "1px solid #e2e6ea",
            borderRadius: 2,
            bgcolor: "#fff",
            boxShadow: "0 3px 12px rgba(31, 45, 61, 0.06)",
            "& > div": { lineHeight: 0 },
          }}
        >
          <AgentsMapComponents
            deliveryData={value === 0 ? [] : filteredData}
            checkinData={value === 0 ? checkinData : []}
          />
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default DeliveryMapView;
